import type { Work, WorkType } from "@/types";
import { works } from "@/mocks/works";

export const allWorks = works;

export function getWorkById(id: string | undefined): Work | undefined {
  if (!id) return undefined;
  return works.find((w) => w.id === id);
}

export function getWorksByType(type: WorkType): Work[] {
  return works.filter((w) => w.type === type);
}

export const allGenres: string[] = Array.from(
  new Set(works.flatMap((w) => w.genres))
).sort((a, b) => a.localeCompare(b, "zh-Hans-CN"));

export const allRegions: string[] = Array.from(
  new Set(works.map((w) => w.region))
).sort((a, b) => a.localeCompare(b, "zh-Hans-CN"));

export const allYears: number[] = Array.from(
  new Set(works.map((w) => w.year))
).sort((a, b) => b - a);

export interface WorkFilters {
  type?: WorkType;
  genres?: string[];
  regions?: string[];
  years?: number[];
}

export function filterWorks(filters: WorkFilters): Work[] {
  const { type, genres = [], regions = [], years = [] } = filters;
  return works.filter((w) => {
    if (type && w.type !== type) return false;
    if (genres.length && !genres.some((g) => w.genres.includes(g))) return false;
    if (regions.length && !regions.includes(w.region)) return false;
    if (years.length && !years.includes(w.year)) return false;
    return true;
  });
}

export type SortMode = "updated" | "popular" | "rating";

export function sortWorks(list: Work[], mode: SortMode): Work[] {
  const copy = [...list];
  if (mode === "popular") return copy.sort((a, b) => b.popularity - a.popularity);
  if (mode === "rating") return copy.sort((a, b) => b.rating - a.rating);
  return copy.sort(
    (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
  );
}

export function searchWorks(query: string): Work[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  return works.filter((w) => {
    const haystack = [w.title, w.titleEn, w.synopsis, w.tagline, w.director, ...w.genres, ...w.cast]
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });
}

export function getRecommendations(work: Work, count = 6): Work[] {
  const scored = works
    .filter((w) => w.id !== work.id)
    .map((w) => {
      const shared = w.genres.filter((g) => work.genres.includes(g)).length;
      const sameType = w.type === work.type ? 1 : 0;
      return { work: w, score: shared * 10 + sameType * 3 + w.rating / 10 };
    })
    .sort((a, b) => b.score - a.score);
  return scored.slice(0, count).map((s) => s.work);
}

export function getFeatured(): Work[] {
  const featured = works.filter((w) => w.featured);
  return featured.length ? featured : works.slice(0, 4);
}